import { StyleSheet } from 'aphrodite/no-important';
import { RATIO } from 'mm-theme-configuration/dist/consts';
import { ImageStyleSheet } from './image.types';

type ImageWidth = 16 | 180 | 360 | 540 | 720 | 1080
type Dpr = 1 | 2 | 3

export interface ImageWidths {
  small: ImageWidth;
  medium: ImageWidth;
  large: ImageWidth;
}

const calculateImageUrl: (host: string, path: string, manipulation?: string) => string = (host, path, manipulation?) => `${host + manipulation}/${path}`;
const isHighDprInLargeImage = (width: number, dpr: number) => width >= 600 && dpr > 2;

export const createImageSrc = (host: string, path: string, width: ImageWidth, aspectRatio: RATIO, dpr: Dpr): string => {
  if (isHighDprInLargeImage(width, dpr)) {
    return createImageSrc(host, path, width, aspectRatio, 2);
  }
  return calculateImageUrl(host, path, `c_fill,w_${width * dpr},ar_${aspectRatio.x}:${aspectRatio.y},f_auto,q_auto,g_auto`);
};

export const getImageSrcAndSrcSet = (imageAspectRatios: ImageAspectRatios, imageWidths: ImageWidths, host: string, path: string) => {
  return {
    srcSetSmallScreen: `${createImageSrc(host, path, imageWidths.small, imageAspectRatios.small, 1)} 1x, ${createImageSrc(host, path, imageWidths.small, imageAspectRatios.small, 2)} 2x, ${createImageSrc(host, path, imageWidths.small, imageAspectRatios.small, 3)} 3x`,
    srcSetMediumScreen: `${createImageSrc(host, path, imageWidths.medium, imageAspectRatios.medium, 1)} 1x, ${createImageSrc(host, path, imageWidths.medium, imageAspectRatios.medium, 2)} 2x, ${createImageSrc(host, path, imageWidths.medium, imageAspectRatios.medium, 3)} 3x`,
    srcSetLargeScreen: `${createImageSrc(host, path, imageWidths.large, imageAspectRatios.large, 1)} 1x, ${createImageSrc(host, path, imageWidths.large, imageAspectRatios.large, 2)} 2x, ${createImageSrc(host, path, imageWidths.large, imageAspectRatios.large, 3)} 3x`,
    lowResUrl: createImageSrc(host, path, 16, { x: 16, y: 9 }, 1),
    fallbackSrc: createImageSrc(host, path, imageWidths.medium, imageAspectRatios.medium, 1),
  };
};

export const imageStyle = StyleSheet.create({
  base: {
    position: 'absolute',
    top: '0',
    left: '0',
    bottom: '0',
    minWidth: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    transition: 'opacity 0.2s ease-in-out',
    objectFit: 'cover',
    minHeight: '100%',
  },
}) as ImageStyleSheet;

export const getImageCaption = (credit: string | undefined, caption: string | undefined): string => {
  return [caption, credit].filter(value => {
    return typeof value === 'string' && value.trim() !== '';
  }).join(' | ');
};

export const createImageWidths = (small: ImageWidth, medium: ImageWidth, large: ImageWidth) => ({
  small,
  medium,
  large,
});

export type ImageHeightCalculationMethodsTypes = 'ASPECT_RATIO' | '100%_HEIGHT';
export interface ImageHeightCalculationMethods {
  small: ImageHeightCalculationMethodsTypes;
  medium: ImageHeightCalculationMethodsTypes;
  large: ImageHeightCalculationMethodsTypes;
}

export const createImageHeightCalculationMethods = (small: ImageHeightCalculationMethodsTypes, medium: ImageHeightCalculationMethodsTypes, large: ImageHeightCalculationMethodsTypes) => ({
  small,
  medium,
  large,
});

export interface ImageAspectRatios {
  small: RATIO;
  medium: RATIO;
  large: RATIO;
}
export const createImageAspectRatios = (small: RATIO, medium: RATIO, large: RATIO) => ({
  small,
  medium,
  large,
});
